import { clsx } from 'clsx';
import { Mic, Square } from 'lucide-react';
import { type Ref, useId, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useDictation } from '../../hooks/useDictation';
import {
  DICTATION_LANGUAGES,
  type DictationLang,
  dictationProblemMessage,
  insertDictation,
  preferredDictationLang,
  saveDictationLang,
} from '../../lib/dictation';
import { Select, Textarea, type TextareaProps } from './Input';

export type VoiceTextareaHandle = {
  /** Stops voice typing, keeping the words still being recognised, and returns the text including them. */
  stop: () => string;
};

export type VoiceTextareaProps = Omit<TextareaProps, 'value' | 'onChange' | 'ref'> & {
  value: string;
  /** `source` tells typed from dictated changes, e.g. to flag a message as dictated. */
  onChange: (value: string, source: 'keyboard' | 'voice') => void;
  ref?: Ref<VoiceTextareaHandle>;
};

/**
 * Textarea with voice typing: the microphone button turns speech into text with the browser's speech recognition and
 * inserts each finished phrase at the cursor. Words still being recognised show greyed under the box. The box stays
 * editable throughout. Call `ref.stop()` before submitting so the last words are kept and the microphone turns off.
 */
export function VoiceTextarea({ value, onChange, ref, disabled, className, ...rest }: VoiceTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  // Where the cursor goes after dictated words, applied once the new value has rendered.
  const caretRef = useRef<number | null>(null);
  const [lang, setLang] = useState(preferredDictationLang);
  const langId = useId();
  const noteId = useId();

  useLayoutEffect(() => {
    valueRef.current = value;
    const caret = caretRef.current;
    caretRef.current = null;
    if (caret !== null) textareaRef.current?.setSelectionRange(caret, caret);
  }, [value]);

  const dictation = useDictation((text) => {
    const current = valueRef.current;
    const textarea = textareaRef.current;
    const start = caretRef.current ?? textarea?.selectionStart ?? current.length;
    const end = caretRef.current ?? textarea?.selectionEnd ?? current.length;
    const edit = insertDictation(current, text, start, end);
    if (edit.value === current) return;
    valueRef.current = edit.value;
    caretRef.current = edit.caret;
    onChange(edit.value, 'voice');
  }, lang);

  useImperativeHandle(ref, () => ({
    stop: () => {
      dictation.stop();
      return valueRef.current;
    },
  }));

  const { supported, listening, interim, problem } = dictation;

  return (
    <div className={clsx('flex flex-col gap-2', className)}>
      <Textarea
        {...rest}
        ref={textareaRef}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          valueRef.current = event.target.value;
          onChange(event.target.value, 'keyboard');
        }}
      />
      {listening && interim.trim() !== '' && (
        <p aria-hidden className="px-1 text-[13px] leading-5 break-words text-slate-400 italic">
          {interim}…
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={listening}
          disabled={disabled || !supported}
          onClick={listening ? dictation.stop : dictation.start}
          className={clsx(
            'inline-flex h-10 shrink-0 items-center gap-2 rounded-md border px-3.5 text-[13px] font-medium shadow-xs transition-colors select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500 disabled:pointer-events-none disabled:opacity-50 sm:h-8 sm:px-3',
            listening
              ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
              : 'border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900',
          )}
        >
          {listening ? <Square size={13} aria-hidden fill="currentColor" /> : <Mic size={16} aria-hidden />}
          {listening ? 'Stop' : 'Start voice typing'}
        </button>
        {supported && (
          <Select
            id={langId}
            aria-label="Voice typing language"
            aria-describedby={noteId}
            aria-invalid={false}
            required={false}
            value={lang}
            options={DICTATION_LANGUAGES}
            onChange={(event) => {
              const next = event.target.value as DictationLang;
              setLang(next);
              saveDictationLang(next);
            }}
            className="w-28"
          />
        )}
        <span role="status" className="flex items-center gap-2 text-xs font-medium text-slate-700">
          {listening && (
            <>
              <span aria-hidden className="relative flex size-2.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
              </span>
              Listening… speak now
            </>
          )}
        </span>
      </div>
      <p id={noteId} aria-live="polite" className={clsx('text-xs', problem && problem !== 'unsupported' ? 'text-red-600' : 'text-slate-500')}>
        {problem
          ? dictationProblemMessage(problem, { touch: window.matchMedia?.('(pointer: coarse)').matches })
          : 'Your browser turns your voice into text (Chrome uses Google, Safari uses Apple). Only the text is saved — no recording.'}
      </p>
    </div>
  );
}
